import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { Task, TaskType } from '../../../../types';
import { useTaskStore } from '../../../../stores/taskStore';
import { useAuthStore } from '../../../../stores/authStore';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { getTeamMemberDisplayName, isTextTooLong, getDisplayLength } from '../../../../utils/userUtils';
import { InlineCalendar } from '../../../../components/ui/InlineCalendar';
import { AutoExpandTextarea } from '../../../../components/ui/AutoExpandTextarea';

interface TaskEditFormProps {
  task: Task;
  taskType?: TaskType;
  onCancel: () => void;
  onSave: () => void;
}

export const TaskEditForm: React.FC<TaskEditFormProps> = ({ 
  task,
  taskType,
  onCancel, 
  onSave 
}) => {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description || '');
  const [acceptance, setAcceptance] = useState(task.acceptance || '');
  const [selectedTaskType, setSelectedTaskType] = useState(task.taskTypeId);
  const [selectedSize, setSelectedSize] = useState(task.size || 'MEDIUM');
  const [selectedPriority, setSelectedPriority] = useState(task.priority || 'MEDIUM');
  const [selectedAssignee, setSelectedAssignee] = useState(task.userId || '');
  const [dueDate, setDueDate] = useState(
    task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : ''
  );

  const { taskTypes, updateTask, teamMembers } = useTaskStore();
  const { user } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();

  // 在个人空间时应该使用空的团队成员列表
  const effectiveTeamMembers = currentWorkspace.type === 'personal' ? [] : teamMembers;
  const titleInputRef = useRef<HTMLInputElement>(null);

  // 自动聚焦到标题输入框
  useEffect(() => {
    titleInputRef.current?.focus();
  }, []);


  // 标题输入框的键盘事件处理器
  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  // 描述和其他输入框的键盘事件处理器
  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCancel();
    }
  };

  const handleSave = useCallback(async () => {
    if (!title.trim()) return;

    // 仅提示标题长度，不阻止保存
    // if (isTextTooLong(title)) {
    //   alert('任务标题过长！中文字符不超过18个，英文字符不超过36个。');
    //   return;
    // }

    try {
      const updateData = {
        title: title.trim(),
        description: description.trim() || undefined,
        acceptance: acceptance.trim(),
        size: selectedSize as 'SMALL' | 'MEDIUM' | 'LARGE',
        priority: selectedPriority as 'HIGH' | 'MEDIUM' | 'LOW',
        taskTypeId: selectedTaskType || undefined,
        userId: selectedAssignee === '' ? null : selectedAssignee,
        dueDate: dueDate ? new Date(dueDate + 'T00:00:00.000Z').toISOString() : null,
      };

      await updateTask(task.id, updateData);
      onSave();
    } catch (error) {
      console.error('更新任务失败:', error);
    }
  }, [title, description, acceptance, selectedSize, selectedPriority, selectedTaskType, selectedAssignee, dueDate, task.id, updateTask, onSave]);

  // 全局键盘事件监听
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      } else if (e.key === 'Enter' && e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [onCancel, handleSave]);

  const handleTaskTypeClick = (taskTypeId: string) => {
    setSelectedTaskType(prev => prev === taskTypeId ? '' : taskTypeId);
  };

  const handleSizeClick = (size: string) => {
    setSelectedSize(size);
  };

  const handlePriorityClick = (priority: string) => {
    setSelectedPriority(priority);
  };

  const handleAssigneeClick = (userId: string) => {
    setSelectedAssignee(prev => prev === userId ? '' : userId);
  };

  return (
    <div className="quick-add-form">
      {/* 标题输入 */}
      <div className="mb-5">
        <label className="text-xs text-gray-500 block mb-1.5">标题</label>
        <input
          ref={titleInputRef}
          type="text"
          className={`w-full text-base rounded-md border border-gray-200 bg-white px-3 py-2 placeholder-gray-400 transition-colors focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 ${
            isTextTooLong(title) ? 'text-red-600' : ''
          }`}
          placeholder="任务标题..."
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleTitleKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          autoComplete="off"
        />
        <div className="flex justify-between items-center mt-1">
          <div className={`text-xs ${
            isTextTooLong(title) ? 'text-red-500' : 'text-gray-400'
          }`}>
            当前长度: {getDisplayLength(title).toFixed(1)}/18
          </div>
          {isTextTooLong(title) && (
            <div className="text-xs text-red-500">
              标题过长
            </div>
          )}
        </div>
      </div>

      {/* 描述输入 */}
      <div className="mb-5">
        <label className="text-xs text-gray-500 block mb-1.5">描述（支持 Markdown）</label>
        <AutoExpandTextarea
          value={description}
          onChange={setDescription}
          onKeyDown={handleInputKeyDown}
          placeholder="添加描述（可选）..."
          maxLength={20000}
          minRows={2}
          maxRows={30}
          focusRows={4}
        />
      </div>

      {/* 完成标准：agent 领任务前必须有 */}
      <div className="mb-5">
        <label className="text-xs text-gray-500 block mb-1.5">完成标准（支持 Markdown；要交给 agent 的任务必须写）</label>
        <AutoExpandTextarea
          value={acceptance}
          onChange={setAcceptance}
          onKeyDown={handleInputKeyDown}
          placeholder="怎么算做好（要交给 agent 的任务必须写）…"
          maxLength={20000}
          minRows={2}
          maxRows={30}
          focusRows={3}
        />
      </div>

      {/* 属性：左边选项，右边日期（与新建弹窗一致） */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_20rem] gap-x-10 gap-y-6 border-t border-gray-100 pt-6 mb-6">
      <div className="space-y-5">
      {/* 任务类型选择 */}
      <div className="mb-3">
        <label className="text-xs text-gray-500 block mb-2">任务类别</label>
        <div className="flex flex-wrap gap-2">
          {taskTypes.map(type => (
            <button
              key={type.id}
              type="button"
              className={`text-xs px-3 py-1 rounded border transition-colors ${
                selectedTaskType === type.id 
                  ? 'border-blue-300 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => handleTaskTypeClick(type.id)}
              style={{
                backgroundColor: selectedTaskType === type.id ? `${type.color}15` : undefined,
                borderColor: selectedTaskType === type.id ? `${type.color}80` : undefined,
                color: selectedTaskType === type.id ? type.color : undefined,
              }}
            >
              {type.name}
            </button>
          ))}
        </div>
      </div>

      {/* 任务大小选择 */}
      <div className="mb-3">
        <label className="text-xs text-gray-500 block mb-2">任务大小</label>
        <div className="flex gap-2">
          {[
            { value: 'SMALL', label: '小 (1-2小时)' },
            { value: 'MEDIUM', label: '中 (半天)' },
            { value: 'LARGE', label: '大 (1天+)' }
          ].map(size => (
            <button
              key={size.value}
              type="button"
              className={`text-xs px-3 py-1 rounded border transition-colors ${
                selectedSize === size.value
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => handleSizeClick(size.value)}
            >
              {size.label}
            </button>
          ))}
        </div>
      </div>

      {/* 优先级选择 */}
      <div className="mb-3">
        <label className="text-xs text-gray-500 block mb-2">优先级</label>
        <div className="flex gap-2">
          {[
            { value: 'HIGH', label: '● 高', color: 'text-red-600' },
            { value: 'MEDIUM', label: '● 中', color: 'text-yellow-600' },
            { value: 'LOW', label: '● 低', color: 'text-green-600' }
          ].map(priority => (
            <button
              key={priority.value}
              type="button"
              className={`text-xs px-3 py-1 rounded border transition-colors ${
                selectedPriority === priority.value
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => handlePriorityClick(priority.value)}
            >
              <span className={priority.color}>{priority.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 分配人选择 - 仅在团队空间显示 */}
      {currentWorkspace.type === 'team' && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 block mb-2">分配给</label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={`text-xs px-3 py-1 rounded border transition-colors ${
                selectedAssignee === ''
                  ? 'bg-blue-50 border-blue-300 text-blue-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => handleAssigneeClick('')}
            >
              未分配
            </button>
            {effectiveTeamMembers.map(member => {
              const displayName = getTeamMemberDisplayName(member, user?.id);
              const isSelected = selectedAssignee === member.userId;

              return (
                <button
                  key={member.userId}
                  type="button"
                  className={`text-xs px-3 py-1 rounded border transition-colors ${
                    isSelected
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleAssigneeClick(member.userId)}
                >
                  {displayName}
                </button>
              );
            })}
          </div>
        </div>
      )}


      </div>
      {/* 到期时间选择 */}
      <div className="mb-3">
        <label className="text-xs text-gray-500 block mb-2">到期时间</label>
        <InlineCalendar
          value={dueDate}
          onChange={setDueDate}
          className="w-full"
        />
      </div>

      </div>

      {/* 操作按钮 */}
      <div className="sticky bottom-0 -mx-1 px-1 pt-3 pb-1 bg-white border-t border-gray-100 flex gap-2 justify-end">
        <button
          type="button"
          className="text-sm px-4 py-1.5 rounded border border-gray-300 hover:bg-gray-50 text-gray-600"
          onClick={onCancel}
        >
          取消 (ESC)
        </button>
        <button
          type="button"
          className={`text-sm px-4 py-1.5 rounded transition-colors ${
            title.trim()
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          onClick={handleSave}
          disabled={!title.trim()}
        >
          保存 (Ctrl+Enter)
        </button>
      </div>
    </div>
  );
};